import React, { useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/CustomAuthContext';
import { useTheme, ThemeName } from '../../contexts/ThemeContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import './Header.css';

const THEME_LABELS: Record<ThemeName, { icon: string; name: string }> = {
  purple: { icon: '🟣', name: 'Purple' },
  dark: { icon: '🌙', name: 'Dark' },
  light: { icon: '🍃', name: 'Light' },
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { flags } = useFeatureFlags();
  const isAdmin = user?.role === 'admin';
  const showFeat = (k: keyof typeof flags) => flags[k] || isAdmin;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <header className="site-header">
      <div className="header-container">
        {/* Mobile menu button */}
        <button
          className={`mobile-menu-btn ${isMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Brand — top-left */}
        <button onClick={() => handleNavigation('/')} className="logo-button" aria-label="PRO Hub home">
          <h1>PRO Hub</h1>
        </button>

        {/* Desktop navigation */}
        <nav className={`header-nav ${isMenuOpen ? 'nav-open' : ''}`}>
          {user ? (
            <>
              {showFeat('feature_page_database') && <NavLink to="/database" onClick={() => setIsMenuOpen(false)} className="nav-link">📖 Pokédex<sup className="beta-badge">BETA</sup></NavLink>}
              {showFeat('feature_page_map') && <NavLink to="/map" onClick={() => setIsMenuOpen(false)} className="nav-link map-link">🗺️ Map<sup className="beta-badge">BETA</sup></NavLink>}
              {showFeat('feature_page_dungeons') && <NavLink to="/dungeons" onClick={() => setIsMenuOpen(false)} className="nav-link">🗺️ Dungeons<sup className="beta-badge">BETA</sup></NavLink>}

              {/* Battle dropdown — Calculator / Counters / Team Builder / PvP */}
              {(showFeat('feature_page_damage_calc') || showFeat('feature_page_counter') || showFeat('feature_page_team_builder') || showFeat('feature_page_pvp')) && (
                <div className="nav-dropdown">
                  <button className="nav-link nav-dropdown-toggle">⚔️ Battle <sup className="beta-badge">BETA</sup> ▾</button>
                  <div className="nav-dropdown-menu">
                    {showFeat('feature_page_damage_calc') && <NavLink to="/damage-calculator" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🧮 Calculator</NavLink>}
                    {showFeat('feature_page_counter') && <NavLink to="/counter" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>⚔️ Counters</NavLink>}
                    {showFeat('feature_page_team_builder') && <NavLink to="/team-builder" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🛡️ Team Builder</NavLink>}
                    {showFeat('feature_page_pvp') && <NavLink to="/pvp" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>⚔️ PvP</NavLink>}
                  </div>
                </div>
              )}

              {/* Games dropdown */}
              <div className="nav-dropdown">
                <NavLink to="/games" onClick={() => setIsMenuOpen(false)} className="nav-link nav-dropdown-toggle">🎮 Games ▾</NavLink>
                <div className="nav-dropdown-menu">
                  <NavLink to="/dex-guesser" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🔍 Dex Guesser</NavLink>
                  <NavLink to="/spin-wheel"  className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🎡 Spin Wheel</NavLink>
                  <NavLink to="/random-team" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🎲 Random Team</NavLink>
                  <NavLink to="/type-quiz"   className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>❓ Type Quiz</NavLink>
                </div>
              </div>

              {/* Admin dropdown */}
              {user.role === 'admin' && (
                <div className="nav-dropdown">
                  <button className="nav-link admin nav-dropdown-toggle">⚙️ Admin ▾</button>
                  <div className="nav-dropdown-menu">
                    <NavLink to="/admin/overview"               className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>📊 Overview</NavLink>
                    <NavLink to="/admin/users"                  className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>👥 Users</NavLink>
                    <NavLink to="/admin/game-data/abilities"    className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🔤 Abilities</NavLink>
                    <NavLink to="/admin/game-data/items"        className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🎒 Items</NavLink>
                    <NavLink to="/admin/game-data/moves"        className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>⚡ Moves</NavLink>
                    <NavLink to="/admin/game-data/pokemon"      className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🐾 Pokémon</NavLink>
                    <NavLink to="/admin/game-data/map-regions"  className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🗺️ Locations</NavLink>
                    <NavLink to="/admin/logging"                className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>📋 Logging</NavLink>
                    <NavLink to="/admin/feature-flags"          className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🚩 Feature Flags</NavLink>
                    <NavLink to="/admin/pvp-stats"              className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>⚔️ PvP Analytics</NavLink>
                    <NavLink to="/admin/atlas"                  className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🗺️ Spawn Atlas</NavLink>
                    <NavLink to="/admin/learnsets"               className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>📖 Learnset Generator</NavLink>
                  </div>
                </div>
              )}

              <div className="user-menu">
                <span className="user-info">{(user as any).displayName || user.username} ({user.role})</span>
                <NavLink to="/profile" onClick={() => setIsMenuOpen(false)} className="profile-btn">Profile</NavLink>
                <button onClick={handleLogout} className="logout-btn">Logout</button>
              </div>
            </>
          ) : (
            <div className="nav-actions">
              {showFeat('feature_page_database') && <NavLink to="/database" onClick={() => setIsMenuOpen(false)} className="nav-link">📖 Pokédex<sup className="beta-badge">BETA</sup></NavLink>}
              {showFeat('feature_page_map') && <NavLink to="/map" onClick={() => setIsMenuOpen(false)} className="nav-link map-link">🗺️ Map<sup className="beta-badge">BETA</sup></NavLink>}
              {(showFeat('feature_page_damage_calc') || showFeat('feature_page_counter') || showFeat('feature_page_team_builder')) && (
                <div className="nav-dropdown">
                  <button className="nav-link nav-dropdown-toggle">⚔️ Battle <sup className="beta-badge">BETA</sup> ▾</button>
                  <div className="nav-dropdown-menu">
                    {showFeat('feature_page_damage_calc') && <NavLink to="/damage-calculator" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🧮 Calculator</NavLink>}
                    {showFeat('feature_page_counter') && <NavLink to="/counter" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>⚔️ Counters</NavLink>}
                    {showFeat('feature_page_team_builder') && <NavLink to="/team-builder" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🛡️ Team Builder</NavLink>}
                  </div>
                </div>
              )}
              <div className="nav-dropdown">
                <NavLink to="/games" onClick={() => setIsMenuOpen(false)} className="nav-link nav-dropdown-toggle">🎮 Games ▾</NavLink>
                <div className="nav-dropdown-menu">
                  <NavLink to="/dex-guesser" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🔍 Dex Guesser</NavLink>
                  <NavLink to="/spin-wheel"  className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🎡 Spin Wheel</NavLink>
                  <NavLink to="/random-team" className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>🎲 Random Team</NavLink>
                  <NavLink to="/type-quiz"   className="nav-dropdown-item" onClick={() => setIsMenuOpen(false)}>❓ Type Quiz</NavLink>
                </div>
              </div>
              <NavLink to="/login" onClick={() => setIsMenuOpen(false)} className="nav-link">Login</NavLink>
              <NavLink to="/register" onClick={() => setIsMenuOpen(false)} className="nav-link">Register</NavLink>
            </div>
          )}
        </nav>

        {/* Theme toggle — far right */}
        <div className="header-right">
          <button
            onClick={toggleTheme}
            className="theme-toggle-btn"
            aria-label={`Switch theme (current: ${THEME_LABELS[theme].name})`}
            title={`Theme: ${THEME_LABELS[theme].name}`}
          >
            <span aria-hidden="true">{THEME_LABELS[theme].icon}</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
